import { User } from '../../../types';

export type DismissalProcedureType = 'pickup' | 'aftercare' | 'walker' | 'special' | 'unassigned';

type PickupPreference = { options?: string[]; otherText?: string } | null;

export const DISMISSAL_TYPE_ORDER: DismissalProcedureType[] = [
  'unassigned',
  'pickup',
  'aftercare',
  'walker',
  'special',
];

export const DISMISSAL_META: Record<DismissalProcedureType, { emoji: string; shortLabel: string }> = {
  pickup: { emoji: '🚗', shortLabel: 'Pickup' },
  aftercare: { emoji: '🏫', shortLabel: 'Aftercare/Staff' },
  walker: { emoji: '🚶', shortLabel: 'Walker' },
  special: { emoji: '⚠️', shortLabel: 'Special' },
  unassigned: { emoji: '?', shortLabel: 'Not assigned' },
};

const toDismissalType = (value: unknown): DismissalProcedureType | null => {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'pickup' || v === 'aftercare' || v === 'walker' || v === 'special' || v === 'unassigned') {
    return v as DismissalProcedureType;
  }
  return null;
};

const mapPickupOptionToDismissal = (option: string): DismissalProcedureType | null => {
  const key = String(option || '').trim().toLowerCase();
  if (key === 'guardian_pickup') return 'pickup';
  if (key === 'aftercare') return 'aftercare';
  if (key === 'bike_walk') return 'walker';
  if (key === 'other') return 'special';
  return null;
};

const getDismissalFromPickupPreference = (pickupPreference: PickupPreference): { type: DismissalProcedureType; otherText?: string } => {
  const pp: any = pickupPreference;
  const options: string[] = Array.isArray(pp)
    ? pp
    : Array.isArray(pp?.options)
      ? pp.options
      : [];
  for (const option of options) {
    const mapped = mapPickupOptionToDismissal(option);
    if (mapped) {
      const otherText = mapped === 'special' && typeof pp?.otherText === 'string'
        ? pp.otherText.trim()
        : '';
      return { type: mapped, ...(otherText ? { otherText } : {}) };
    }
  }
  return { type: 'unassigned' };
};

export const resolveStudentDismissal = (
  student: User
): { type: DismissalProcedureType; tooltip: string; otherText?: string } => {
  const manualType = toDismissalType((student as any)?.dismissalProcedureType);
  const manualOtherText = String((student as any)?.dismissalProcedureNote || '').trim();

  const parentDefault = getDismissalFromPickupPreference((student as any)?.pickupPreference || null);
  const type = manualType || parentDefault.type;
  const otherText = manualType === 'special'
    ? (manualOtherText || parentDefault.otherText || '')
    : (parentDefault.otherText || '');

  if (type === 'pickup') {
    return { type, tooltip: 'Dismissed to guardian pickup.' };
  }
  if (type === 'aftercare') {
    return { type, tooltip: 'Dismissed to aftercare/staff.' };
  }
  if (type === 'walker') {
    return { type, tooltip: 'Dismissed to bike/walk home.' };
  }
  if (type === 'special') {
    return {
      type,
      tooltip: otherText ? `Dismissed to ${otherText}.` : 'Dismissed to special procedure.',
      ...(otherText ? { otherText } : {}),
    };
  }
  return { type: 'unassigned', tooltip: 'Dismissal not assigned yet.' };
};

export const getNextDismissalType = (currentType: DismissalProcedureType): DismissalProcedureType => {
  const idx = DISMISSAL_TYPE_ORDER.indexOf(currentType);
  if (idx < 0 || idx === DISMISSAL_TYPE_ORDER.length - 1) return DISMISSAL_TYPE_ORDER[0];
  return DISMISSAL_TYPE_ORDER[idx + 1];
};
